/**
 * src/ui/login.ts — `/entrar`: a landing pública e, no mesmo documento, o login.
 *
 * Sem sessão o site abre aqui. A landing explica o produto e leva aos dois
 * caminhos que já existiam: mostrar o QR nesta tela (`onLinkByQr`) ou entrar
 * para gerenciar. Cadastro continua no mesmo formulário. Nada de `innerHTML`
 * com dado do servidor (HYBRID §8).
 *
 * O smoke exige, neste nó: `data-papel="login"`, `input[name=email]`,
 * `input[name=password]` e `button[type=submit]`.
 */
import { ApiError, type Session } from '../../net';

function el(tag: string, className?: string, text?: string): HTMLElement {
  const node = document.createElement(tag);
  if (className) {
    node.className = className;
  }
  if (text !== undefined) {
    node.textContent = text;
  }
  return node;
}

function campo(label: string, type: string, name: string): HTMLElement {
  const wrap = el('label', 'campo');
  wrap.append(el('span', 'campo__rotulo', label));
  const input = document.createElement('input');
  input.type = type;
  input.name = name;
  input.autocomplete = name === 'email' ? 'email' : name === 'password' ? 'current-password' : 'name';
  input.className = 'campo__input';
  wrap.append(input);
  return wrap;
}

function passo(numero: string, titulo: string, texto: string): HTMLElement {
  const item = el('li', 'land__passo');
  item.append(el('span', 'land__numero', numero));
  item.append(el('h3', 'land__passo-titulo', titulo));
  item.append(el('p', 'land__passo-texto', texto));
  return item;
}

export interface LoginOptions {
  onDone: () => void;
  /** Abre a tela de vínculo por QR (TV/TV Box sem teclado). */
  onLinkByQr: () => void;
}

/** Desenha a landing. `onDone` dispara depois de login/signup OK. */
export function renderLogin(session: Session, options: LoginOptions): HTMLElement {
  const root = el('section', 'pagina pagina--entrar land');
  root.dataset['papel'] = 'login';

  const nome = campo('Empresa', 'text', 'name');
  nome.hidden = true;
  const email = campo('E-mail', 'email', 'email');
  const senha = campo('Senha', 'password', 'password');

  const form = document.createElement('form');
  form.className = 'formulario land__form';
  form.id = 'conta';
  form.noValidate = true;
  form.append(el('h2', 'land__form-titulo', 'Entrar na conta'));
  form.append(
    el(
      'p',
      'land__form-texto',
      'No computador ou no celular. Publicar anúncio continua no app.',
    ),
  );
  form.append(email, senha, nome);

  const erro = el('p', 'formulario__erro');
  erro.dataset['papel'] = 'erro';
  erro.hidden = true;
  form.append(erro);

  const entrar = document.createElement('button');
  entrar.type = 'submit';
  entrar.className = 'botao botao--primario';
  entrar.textContent = 'Entrar';
  form.append(entrar);

  let cadastro = false;
  const alternar = document.createElement('button');
  alternar.type = 'button';
  alternar.className = 'botao botao--secundario';
  alternar.textContent = 'Não tenho conta — criar agora';
  alternar.addEventListener('click', () => {
    cadastro = !cadastro;
    nome.hidden = !cadastro;
    entrar.textContent = cadastro ? 'Criar conta e entrar' : 'Entrar';
    alternar.textContent = cadastro ? 'Já tenho conta — entrar' : 'Não tenho conta — criar agora';
    erro.hidden = true;
  });
  form.append(alternar);

  const valor = (wrap: HTMLElement): string =>
    (wrap.querySelector('input') as HTMLInputElement | null)?.value.trim() ?? '';

  form.addEventListener('submit', event => {
    event.preventDefault();
    if (form.dataset['enviando'] === 'sim') {
      return;
    }
    form.dataset['enviando'] = 'sim';
    entrar.disabled = true;
    erro.hidden = true;
    const done = (): void => {
      form.dataset['enviando'] = 'nao';
      entrar.disabled = false;
    };
    const falha = (error: unknown): void => {
      const code = (error as ApiError)?.code;
      erro.textContent = code === 'EMAIL_ALREADY_REGISTERED'
        ? 'Esse e-mail já tem conta. Tente entrar.'
        : code === 'INVALID_CREDENTIALS'
          ? 'E-mail ou senha incorretos.'
          : code === 'RATE_LIMITED'
            ? 'Muitas tentativas. Aguarde um pouco e tente de novo.'
            : 'Não foi possível entrar agora. Tente de novo.';
      erro.hidden = false;
      done();
    };
    const request = cadastro
      ? session.signup(valor(nome), valor(email), valor(senha))
      : session.login(valor(email), valor(senha));
    void request.then(options.onDone).catch(falha);
  });

  const focarConta = (): void => {
    form.scrollIntoView({ behavior: 'smooth', block: 'center' });
    (email.querySelector('input') as HTMLInputElement | null)?.focus();
  };

  const botaoQr = (rotulo: string, primario: boolean): HTMLButtonElement => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = primario ? 'botao botao--primario' : 'botao botao--secundario';
    button.textContent = rotulo;
    button.addEventListener('click', options.onLinkByQr);
    return button;
  };

  const nav = el('header', 'land__nav');
  const marca = el('p', 'land__marca', 'Simple Indoor');
  const navAcoes = el('div', 'land__nav-acoes');
  const navEntrar = document.createElement('button');
  navEntrar.type = 'button';
  navEntrar.className = 'botao botao--secundario';
  navEntrar.textContent = 'Entrar';
  navEntrar.addEventListener('click', focarConta);
  navAcoes.append(navEntrar, botaoQr('Sou a TV', true));
  nav.append(marca, navAcoes);

  const hero = el('div', 'land__hero');
  const heroTexto = el('div', 'land__hero-texto');
  heroTexto.append(el('p', 'land__kicker', 'Mídia indoor simples'));
  heroTexto.append(el('h1', 'land__titulo', 'Anúncios na TV, do celular.'));
  heroTexto.append(
    el(
      'p',
      'land__lead',
      'Abra este site na TV ou use o app instalado. Aprove no celular e publique uma foto ou um vídeo. A tela troca sozinha.',
    ),
  );
  const heroAcoes = el('div', 'land__acoes');
  heroAcoes.append(botaoQr('Mostrar QR nesta tela', true));
  const heroEntrar = document.createElement('button');
  heroEntrar.type = 'button';
  heroEntrar.className = 'botao botao--secundario';
  heroEntrar.textContent = 'Já tenho conta';
  heroEntrar.addEventListener('click', focarConta);
  heroAcoes.append(heroEntrar);
  heroTexto.append(heroAcoes);

  const tv = el('div', 'land__moldura');
  tv.setAttribute('aria-hidden', 'true');
  const tela = el('div', 'land__tela');
  tela.append(el('p', 'land__tela-loja', 'Loja'));
  tela.append(el('p', 'land__tela-oferta', 'Promoção do dia'));
  tela.append(el('p', 'land__tela-nota', 'A programação chega sozinha'));
  tv.append(tela);
  hero.append(heroTexto, tv);

  const passos = el('ol', 'land__passos');
  passos.append(
    passo('1', 'Abra na TV', 'Neste navegador, mostre o QR. No app da TV, aparece um código de 6 números.'),
    passo('2', 'Aprove no celular', 'A câmera do celular lê o QR. O código de 6 números você digita no app.'),
    passo('3', 'Publique o anúncio', 'Foto ou vídeo, as telas e quando exibir. Sem montar playlist.'),
  );

  const grade = el('ul', 'land__grade');
  for (const [titulo, texto] of [
    ['Telas', 'Cada TV, TV Box ou navegador pareado.'],
    ['Anúncios', 'A foto ou o vídeo que a loja vai ver.'],
    ['Quando exibir', 'Agora, um período ou toda semana.'],
  ] as const) {
    const item = el('li', 'land__conceito');
    item.append(el('h3', 'land__passo-titulo', titulo));
    item.append(el('p', 'land__passo-texto', texto));
    grade.append(item);
  }

  root.append(nav, hero, passos, grade, form);
  return root;
}
